import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Header.css';
import logo from '../../assets/images/logo.png';
import Login from '../../pages/Auth/Login';
import Register from '../../pages/Auth/Register';
import ChangePassword from '../../pages/Auth/ChangePassword';
import ForgotPassword from '../../pages/Auth/ForgotPassword';
import ResetPassword from '../../pages/Auth/ResetPassword';
import VerifyOTP from '../../pages/Auth/VerifyOTP';
import TrainerRegister from '../../pages/Auth/TrainerRegister';
import CounselorRegister from '../../pages/Auth/CounselorRegister';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalType, setAuthModalType] = useState('login');
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [demoForm, setDemoForm] = useState({ name: '', email: '', phone: '', course: '' });
  const [demoError, setDemoError] = useState('');
  const [demoSuccess, setDemoSuccess] = useState('');
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  // Close modal on ESC
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setShowAuthModal(false);
    };
    if (showAuthModal) document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showAuthModal]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Modal background blur
  useEffect(() => {
    if (showAuthModal) {
      document.body.classList.add('modal-blur');
    } else {
      document.body.classList.remove('modal-blur');
    }
    return () => document.body.classList.remove('modal-blur');
  }, [showAuthModal]);

  const handleDemoInputChange = (e) => {
    setDemoForm({ ...demoForm, [e.target.name]: e.target.value });
    setDemoError('');
    setDemoSuccess('');
  };

  const handleDemoSubmit = (e) => {
    e.preventDefault();
    // Simple validation
    if (!demoForm.name || !demoForm.email || !demoForm.phone || !demoForm.course) {
      setDemoError('Please fill in all fields.');
      return;
    }
    setDemoSuccess('Thank you! We will contact you soon.');
    setTimeout(() => {
      setShowDemoModal(false);
      setDemoForm({ name: '', email: '', phone: '', course: '' });
      setDemoSuccess('');
    }, 1500);
  };

  const openAuthModal = (type) => {
    setShowAuthModal(true);
    setAuthModalType(type);
  };

  return (
    <>
      <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
        <div className="header-content container">
          {/* Left: Logo */}
          <Link to="/" className="logo">
            <img src={logo} alt="LetsCode Brain" className="logo-image" />
          </Link>

          {/* Middle: Navigation */}
          <nav className={`main-nav ${isMenuOpen ? 'active' : ''}`}> 
            <ul>
              <li>
                <Link to="/" className={`nav-link ${location.pathname === '/' ? 'active' : ''}`}>Home</Link>
              </li>
              <li>
                <Link to="/about" className={`nav-link ${location.pathname === '/about' ? 'active' : ''}`}>About</Link>
              </li>
              <li>
                <Link to="/courses" className={`nav-link ${location.pathname === '/courses' ? 'active' : ''}`}>Courses</Link>
              </li>
              <li>
                <Link to="/specialization" className={`nav-link ${location.pathname === '/specialization' ? 'active' : ''}`}>Specialization</Link>
              </li>
              <li>
                <Link to="/contact" className={`nav-link ${location.pathname === '/contact' ? 'active' : ''}`}>Contact</Link>
              </li>
            </ul>
          </nav>

          {/* Middle blank space for separation */}
          <div style={{ flex: 1 }}></div>

          {/* Right: Auth Buttons */}
          <div className="header-actions">
            <button className="btn btn-outline" style={{ marginRight: '10px' }} onClick={() => openAuthModal('login')}>Sign In</button>
            <button className="btn btn-primary" onClick={() => setShowDemoModal(true)}>
              Book a Demo
            </button>
            {/* Demo: Add buttons to open all auth modals for testing */}
            <button style={{display:'none'}} onClick={() => openAuthModal('register')}>Register</button>
            <button style={{display:'none'}} onClick={() => openAuthModal('trainer-register')}>Trainer Register</button>
            <button style={{display:'none'}} onClick={() => openAuthModal('counselor-register')}>Counselor Register</button>
            <button style={{display:'none'}} onClick={() => openAuthModal('forgot-password')}>Forgot Password</button>
            <button style={{display:'none'}} onClick={() => openAuthModal('reset-password')}>Reset Password</button>
            <button style={{display:'none'}} onClick={() => openAuthModal('verify-otp')}>Verify OTP</button>
            <button style={{display:'none'}} onClick={() => openAuthModal('change-password')}>Change Password</button>
            <button 
              className="mobile-menu-btn"
              onClick={toggleMenu}
              aria-label="Toggle menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
        </div>
      </header>

      {/* Auth Modal */}
      {showAuthModal && (
        <div className="modal-overlay" onClick={() => setShowAuthModal(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-tabs">
              {/* Tabs for login/register only, others just show close */}
              {(authModalType === 'login' || authModalType === 'register') ? (
                <>
                  <button className={authModalType === 'login' ? 'active' : ''} onClick={() => setAuthModalType('login')}>Login</button>
                  <button className={authModalType === 'register' ? 'active' : ''} onClick={() => setAuthModalType('register')}>Register</button>
                </>
              ) : (
                <span style={{flex:1}}></span>
              )}
              <button className="modal-close" onClick={() => setShowAuthModal(false)}>&times;</button>
            </div>
            <div className="modal-content">
              {authModalType === 'login' && <Login inModal={true} onSuccess={() => setShowAuthModal(false)} />}
              {authModalType === 'register' && <Register inModal={true} onSuccess={() => setShowAuthModal(false)} />}
              {authModalType === 'trainer-register' && <TrainerRegister inModal={true} onSuccess={() => setShowAuthModal(false)} />}
              {authModalType === 'counselor-register' && <CounselorRegister inModal={true} onSuccess={() => setShowAuthModal(false)} />}
              {authModalType === 'forgot-password' && <ForgotPassword inModal={true} onSuccess={() => setShowAuthModal(false)} />}
              {authModalType === 'reset-password' && <ResetPassword inModal={true} onSuccess={() => setShowAuthModal(false)} />}
              {authModalType === 'verify-otp' && <VerifyOTP inModal={true} onSuccess={() => setShowAuthModal(false)} />}
              {authModalType === 'change-password' && <ChangePassword inModal={true} onSuccess={() => setShowAuthModal(false)} />}
            </div>
          </div>
        </div>
      )}

      {showDemoModal && (
        <div className="modal-overlay" onClick={() => setShowDemoModal(false)}>
          <div className="modal-box demo-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowDemoModal(false)}>&times;</button>
            <h2 className="modal-title">Book a Demo</h2>
            <form className="demo-form" onSubmit={handleDemoSubmit}>
              <label>Name
                <input type="text" name="name" value={demoForm.name} onChange={handleDemoInputChange} required />
              </label>
              <label>Email
                <input type="email" name="email" value={demoForm.email} onChange={handleDemoInputChange} required />
              </label>
              <label>Phone
                <input type="tel" name="phone" value={demoForm.phone} onChange={handleDemoInputChange} required />
              </label>
              <label>Course
                <input type="text" name="course" value={demoForm.course} onChange={handleDemoInputChange} required />
              </label>
              {demoError && <div className="form-error">{demoError}</div>}
              {demoSuccess && <div className="form-success">{demoSuccess}</div>}
              <button type="submit" className="btn btn-primary demo-submit-btn">Submit</button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;